import { Component, HostListener, Inject, OnInit } from '@angular/core';
import { BaseCustomDialog } from '../base-custom-dialog';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ACTION_DATA_SERVICE, ActionDataService } from '../../../../services/action/data.service';
import { ComposerOfferOrOrder, IActionTypes } from '../../../../models/action.model';
import { DateTimeHelper, TimePeriodType } from '../../../../helper/date-time.helper';
import { ActionIconHelper } from '../../../../helper/action-icon.helper';
import { LEADS_DATA_SERVICE, LeadsDataService } from '../../../../services/leads/leads-data.service';
import {
  ConstructionTypes,
  ExtendedProperties, ICompanyContacts,
  IContact,
  LeadComposerChangesModel, LeadsDataAction, LeadsHistory,
  ProfileStudio, SelectedComposerOffers
} from '../../../../models/leads/leads.model';
import { AppointmentsHelper } from '../../../../helper/appointments.helper';
import { UserTokenService } from '../../../../services/user-service/user-token.service';
import { ConvertHelper } from "../../../../helper/convert.helper";
import { PROFILE_DATA_SERVICE, ProfileDataService } from '../../../../services/profile/profile-data.service';
import { LAST_SELECTED_STUDIO_ID } from '../../../../constants/constants';
import { LeadHelper } from '../../../../helper/lead.helper';
import { CurrencyHelper } from '../../../../helper/app-currency';

export interface OfferSubmitDialogData {
  isExternal?: boolean,
  contactName: string,
  leadId: number,
  leadTypeId: number,
  title: string,
  externalEmail: string,
  loadedLeadComposerOffers: LeadComposerChangesModel[],
  onAction?: ( name: string, label: string, callback?: () => void ) => any,
}

@Component({
  selector: 'fi-offer-submit-popup',
  templateUrl: './offer-submit-popup.component.html',
  styleUrls: ['./offer-submit-popup.component.scss'],
  standalone: false,
})
export class OfferSubmitPopupComponent extends BaseCustomDialog implements OnInit {
  contactName = '';
  leadId: number;
  selectedDateOptionId: number;
  appointmentTypeId: number;
  showroomAppointment: IActionTypes;
  showroomTypeId: string;

  readonly dateOptionTypes = DateTimeHelper.datePeriodTypes;

  appointmentOptionTypes: IActionTypes[] = [];
  showroomTypes: ProfileStudio[] = [];
  searchedText = '';
  note = '';
  selectedDate: Date;
  title: string;
  iconUrl = ActionIconHelper.getActionIconUrl('OFFER_SUBMIT', false);
  darkIconUrl = ActionIconHelper.getActionIconUrl('OFFER_SUBMIT', true);
  submitting: boolean;
  companyContacts: ICompanyContacts[];
  contactToDisplay: IContact;
  contact: IContact;
  appointmentExists = false;
  leadOfferId = LeadHelper.LEAD_OFFER_ID;
  leadTypeId: number;
  extendedProperties: ExtendedProperties = {};
  currencies = CurrencyHelper.FINSTRAL_CURRENCIES;
  currencyId: number;
  offerAmount: string;
  inValidNumber = false;
  inValidNote = false;
  inValidOfferAmount = false;
  loadedLeadComposerOffers: LeadComposerChangesModel[];
  checkedComposerOffers: SelectedComposerOffers = {};
  composerMainOffers: LeadComposerChangesModel[] = [];
  isExternal = false;
  onAction?: ( name: string, label: string, successCallback?: () => void ) => any

  constructor(
    private userTokenService: UserTokenService,
    public dialogRef: MatDialogRef<OfferSubmitPopupComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: OfferSubmitDialogData,
    @Inject(ACTION_DATA_SERVICE) private actionDataService: ActionDataService,
    @Inject(LEADS_DATA_SERVICE) private leadsDataService: LeadsDataService,
    @Inject(PROFILE_DATA_SERVICE) private profileDataService: ProfileDataService,
  ) {
    super(dialogRef);
    if ( dialogData?.loadedLeadComposerOffers?.length ) {
      this.loadedLeadComposerOffers = dialogData.loadedLeadComposerOffers;
      this.getOrdersAndOffersFromComposer(this.loadedLeadComposerOffers);
    }

    this.contactName = dialogData?.contactName || '';
    this.leadId = dialogData?.leadId;
    this.title = dialogData?.title;
    this.onAction = dialogData.onAction;
    this.leadTypeId = dialogData?.leadTypeId;

    this.isExternal = this.dialogData?.isExternal;

    if ( !dialogData?.loadedLeadComposerOffers ) {
      leadsDataService.getLeadComposerOffers(this.leadId).subscribe(( { success, data } ) => {
        if ( success ) {
          this.loadedLeadComposerOffers = data;
          this.getOrdersAndOffersFromComposer(this.loadedLeadComposerOffers);
        }
      });
    }

    if ( this.dialogData?.externalEmail ) {
      this.darkIconUrl = ActionIconHelper.getActionIconUrl('OFFER_SUBMIT_EXTERNAL', true);
    }

    if ( this.leadTypeId === this.leadOfferId ) {
      this.leadsDataService.getExtendedPropertiesByLeadId(this.leadId).subscribe(( { success, data } ) => {
        if ( success ) {
          this.extendedProperties = data;
          this.getCurrencyIdByName();
        }
      });
    }

    this.actionDataService.getAppointmentTypes().subscribe(( { success, data } ) => {
      if ( success ) {
        this.appointmentOptionTypes = data;
        this.showroomAppointment = this.appointmentOptionTypes.find(item => item.Name === 'SCHAURAUM');
      }
    });

    this.profileDataService.getStudios().subscribe(( { success, data } ) => {
      if ( success ) {
        this.showroomTypes = data;
        if ( !this.isExternal ) {
          this.showroomTypeId = sessionStorage.getItem(LAST_SELECTED_STUDIO_ID) ?? '';
        }
      }
    });

    this.leadsDataService.getLeadById(this.leadId).subscribe(( { success, data } ) => {
      if ( success ) {
        this.companyContacts = data.CompanyContacts;
        this.contactToDisplay = data.Contact;
      }
    });
  }

  private getOrdersAndOffersFromComposer( items: LeadComposerChangesModel[] ): void {
    this.composerMainOffers = [];

    items.forEach(item => {
      if ( !item.IsOrder && item.IsMain ) {
        this.composerMainOffers.push(item);
      }
    })
  }

  onDateSelect( date: Date, time = false ): void {
    this.selectedDateOptionId = null;

    if ( !date ) {
      this.selectedDate = null;
      return;
    }

    if ( !this.selectedDate ) {
      this.selectedDate = date;
    } else if ( !time ) {
      this.selectedDate.setFullYear(date.getFullYear(), date.getMonth(), date.getDate());
    } else {
      this.selectedDate.setHours(date.getHours(), date.getMinutes());
    }

    this.selectedDate = new Date(this.selectedDate);

    this.appointmentExists = AppointmentsHelper.getAppointments(this.selectedDate, this.appointmentExists);
  }

  setPeriods( $event ): void {
    this.selectedDateOptionId = $event;
    this.selectedDate = DateTimeHelper.getDateAfterPeriod(this.dateOptionTypes.find(item => item.Id === +$event));
    this.appointmentExists = AppointmentsHelper.getAppointments(this.selectedDate, this.appointmentExists);
  }

  onDateOptionSelected( type: TimePeriodType ): void {
    this.selectedDateOptionId = type.Id;
    this.selectedDate = DateTimeHelper.getDateAfterPeriod(type);
    this.appointmentExists = AppointmentsHelper.getAppointments(this.selectedDate, this.appointmentExists);
  }

  async ngOnInit(): Promise<void> {
    const userId = await this.userTokenService.getId();
    AppointmentsHelper.getDbAppointments(this.actionDataService, userId);
  }

  setExtendedProperties( currency: string ): void {
    this.currencies.forEach(el => {
      if ( el.Id.toString() === currency ) {
        this.extendedProperties.OFFER_CURRENCY = el.Description;
      }
    })
  }

  getCurrencyIdByName(): void {
    this.currencies.forEach(el => {
      if ( el.Description === this.extendedProperties.OFFER_CURRENCY ) {
        this.currencyId = el.Id;
      }
    })
  }

  showRoomText = ( item: ProfileStudio ) => `${item.KKZ} - ${item.Name} - ${item.City}`;

  closeDialog(): void {
    this.clearClassAndCloseDialog(this.dialogRef);
  }

  async onWarningClick( ev: MouseEvent ): Promise<void> {
    if ( ev.target instanceof HTMLElement && ev.target.closest('a') ) {
      const link = ev.target.closest('a');
      const url = new URL(link.href);
      const label = link.textContent.replace(/[^\p{L}\s]/gmu, '').trim();
      const actionName = url.hash.replace('#', '');

      if ( !url.hostname || url.hostname === document.location.hostname ) {
        ev.preventDefault();
        this.dialogRef.close();
        setTimeout(() => {
          this.onAction?.(
            actionName,
            label,
            () => null
          );
        }, 80)
      }
    }
  }

  get formIsValid(): boolean {
    const hasRequiredFields = !!this.leadId && !!this.selectedDate && !!this.appointmentTypeId;
    // checks if selected appointment is showroom making that field mandatory
    const isShowroomRequired = !this.isExternal && this.appointmentTypeId === this.showroomAppointment?.Id ? !!this.showroomTypeId : true;
    const isNotSubmitting = !this.submitting;
    const isNoteValid = !this.inValidNote;

    return hasRequiredFields && isShowroomRequired && isNotSubmitting && isNoteValid;
  }

  get numberIsValid(): boolean {
    if ( !this.extendedProperties.OFFER_NUMBER ) {
      return true;
    }
    this.inValidNumber = !this.extendedProperties.OFFER_NUMBER.match(/^\d{5,10}A$/m);
    return !this.inValidNumber;
  }

  sumIsValid(): boolean {
    if ( !this.extendedProperties.OFFER_AMOUNT ) {
      return true;
    }
    this.inValidOfferAmount = !(this.extendedProperties.OFFER_AMOUNT + '').match(/^\d+(?:[,.]\d+)*$/g);
    return !this.inValidOfferAmount;
  }

  inValidMaxLength(): boolean {
    this.inValidNote = this.note.length > 2000;
    return this.inValidNote;
  }

  submit(): void {
    let sumIsValid = this.sumIsValid();
    if ( this.formIsValid && this.numberIsValid && sumIsValid ) {
      this.submitting = true;
      const offerExtProp = {
        OFFER_AMOUNT: !this.offerAmount ? this.extendedProperties.OFFER_AMOUNT : ConvertHelper.currencyToNumber(this.offerAmount),
        OFFER_CURRENCY: this.extendedProperties.OFFER_CURRENCY,
        OFFER_NUMBER: this.extendedProperties.OFFER_NUMBER
      };

      this.actionDataService.submitOffer(
        this.leadId,
        this.note,
        this.appointmentTypeId,
        this.selectedDate,
        offerExtProp,
        false,
        !this.isExternal && this.appointmentTypeId === this.showroomAppointment?.Id ? this.showroomTypeId : undefined,
        !!this.dialogData.externalEmail
      )
        .subscribe(( { success, data } ) => {
          if ( success ) {
            this.clearClassAndCloseDialog(this.dialogRef, 'remove');
          }

          if ( !this.isExternal && this.appointmentTypeId === this.showroomAppointment?.Id ) {
            sessionStorage.setItem(LAST_SELECTED_STUDIO_ID, this.showroomTypeId);
          }

          this.submitting = false;
        });
    }
  }

  private checkComposerDataByDefault( loadedLeadComposerOffers: LeadComposerChangesModel[] ): void {
    loadedLeadComposerOffers?.forEach(offer => {
      this.checkedComposerOffers[offer.Name] = true;
    })
  }
}
